import React, { ReactNode } from "react";

import { ReactComponent as AskAiIcon } from "@common/assets/images/ask-ai-icon.svg";
import { ReactComponent as BookIcon } from "@common/assets/images/book-icon.svg";
import { ReactComponent as ChromeImage } from "@common/assets/images/chrome-lineart.svg";
import { ReactComponent as NoteIcon } from "@common/assets/images/note-write-icon.svg";
import { ReactComponent as PlusIcon } from "@common/assets/images/shared-icons/plus-icon.svg";
import Tooltip from "@common/components/Tooltip/Tooltip";
import environments from "@common/environments";
import { showShake } from "@common/util/shakeSdkUtils";
import { SHAKE_EXTENSION_PAGE } from "@common/util/urls";
import { useScreenDimensions } from "@main/pages/shared/consumers/useScreenDimensions";
import { RoutePaths as MainPaths, RoutePaths } from "@main/router/config/routePaths";
import environment from "common/environments";
import { generatePath, useNavigate, useParams } from "react-router-dom";

import * as Styled from "./QuickAccessButtons.styles";
import { shortcutModifierLabel, useQuickAccessShortcut } from "./useQuickAccessShortcut";

export const QuickAccessButtons = () => {
  const navigate = useNavigate();
  const { workspaceSlug } = useParams();

  const { shouldHideLayoutViews } = useScreenDimensions();

  const onAddNewAppClick = () => {
    if (workspaceSlug) {
      navigate(generatePath(RoutePaths.ADD_APP, { workspaceSlug: workspaceSlug }));
    } else {
      window.open(`${environments.appURL}${MainPaths.SIGNUP}`, "_blank")?.focus();
    }
  };

  const onAskAIClick = () => {
    window.openInkeep();
  };

  const onSendUsFeedbackClick = () => {
    showShake();
  };

  const openDocumentation = () => {
    window.open(environment.documentationUrl, "_blank");
  };

  const openChromeExtensionPage = () => {
    window.open(SHAKE_EXTENSION_PAGE, "_blank");
  };

  return (
    <Styled.QuickAccessWrapper $hide={shouldHideLayoutViews}>
      <QuickAccessButton
        tooltipText={"Add new app"}
        onClick={onAddNewAppClick}
        shortcutLetter={"A"}
        icon={<PlusIcon />}
        enabled={!shouldHideLayoutViews}
      />

      <QuickAccessButton
        tooltipText={"Ask AI"}
        onClick={onAskAIClick}
        shortcutLetter={"B"}
        icon={<AskAiIcon />}
        enabled={!shouldHideLayoutViews}
      />

      <QuickAccessButton
        tooltipText={"Install Chrome extension"}
        onClick={openChromeExtensionPage}
        shortcutLetter={"C"}
        icon={<ChromeImage />}
        enabled={!shouldHideLayoutViews}
      />

      <QuickAccessButton
        tooltipText={"Read documentation"}
        onClick={openDocumentation}
        shortcutLetter={"D"}
        icon={<BookIcon />}
        enabled={!shouldHideLayoutViews}
      />

      <QuickAccessButton
        tooltipText={"Send us feedback"}
        onClick={onSendUsFeedbackClick}
        shortcutLetter={"E"}
        icon={<NoteIcon />}
        enabled={!shouldHideLayoutViews}
      />
    </Styled.QuickAccessWrapper>
  );
};

interface QuickAccessButtonProps {
  tooltipText: string;
  onClick: () => void;
  icon: ReactNode;
  shortcutLetter: string;
  enabled: boolean;
}

const QuickAccessButton = ({ tooltipText, onClick, shortcutLetter, icon, enabled }: QuickAccessButtonProps) => {
  useQuickAccessShortcut({ letter: shortcutLetter, onTrigger: onClick, enabled });

  return (
    <Styled.QuickAccessButtonWrap>
      <Tooltip
        text={`Shortcut  •  ${tooltipText}  •  ${shortcutModifierLabel}+Shift+${shortcutLetter}`}
        position={"left"}
        prewrap
      >
        <Styled.QuickAccessButton onClick={onClick}>{icon}</Styled.QuickAccessButton>
      </Tooltip>
      <Styled.ShortcutLetter onClick={onClick}>{shortcutLetter}</Styled.ShortcutLetter>
    </Styled.QuickAccessButtonWrap>
  );
};
